import { useState } from "react";
import { toast } from "@volca/ui/lib/toast";
import { getErrorMessage } from "@/core/api/errors";
import {
  useSavePayrollStatementNote,
  useSendPayrollStatementNotifications,
} from "./query";
import type {
  PayrollStatementNotificationChannel,
  PayrollStatementNotificationDelivery,
  PayrollStatementNotificationRecipient,
  PayrollStatementNotificationSendResponse,
} from "./types";
import {
  getFailedPayrollStatementDeliveries,
  getTextableRecipientsCount,
  isRenotifyOnly,
} from "./utils";

const technicians = (count: number) =>
  `${count} ${count === 1 ? "technician" : "technicians"}`;

/** The toast after a send, naming only the channels it used. */
export const getSentDescription = ({
  count,
  channels,
  textCount,
}: {
  count: number;
  channels: PayrollStatementNotificationChannel[] | null;
  textCount: number;
}) => {
  if (channels && !channels.includes("email"))
    return `${technicians(count)} can see their PayPack; ${textCount} ${
      textCount === 1 ? "gets" : "get"
    } a text about it.`;
  return `${technicians(count)} will get their PayPack by email${
    textCount > 0 ? `; ${textCount} also by text` : ""
  }.`;
};

export const useSendPayrollStatements = ({
  businessId,
  periodStart,
  previewOnly,
  sendDisabledReason,
  selectedRecipients,
  noteDrafts,
  trackSubmittedNotes,
}: {
  businessId: string | null;
  periodStart: string | null;
  previewOnly: boolean;
  sendDisabledReason: string | null;
  selectedRecipients: PayrollStatementNotificationRecipient[];
  noteDrafts: Record<string, string>;
  trackSubmittedNotes: (
    deliveries: PayrollStatementNotificationDelivery[],
  ) => void;
}) => {
  const [sendFailures, setSendFailures] = useState<
    PayrollStatementNotificationDelivery[]
  >([]);
  const [activeSendResponse, setActiveSendResponse] =
    useState<PayrollStatementNotificationSendResponse | null>(null);
  const { mutateAsync: sendNotifications, isPending: isSending } =
    useSendPayrollStatementNotifications(businessId, periodStart);
  const { mutateAsync: saveNote, isPending: isSavingNote } =
    useSavePayrollStatementNote(businessId, periodStart);

  /**
   * @param channels the channels chosen in the dialog, or null when the business
   *   only emails and there was nothing to choose
   */
  const send = async (
    channels: PayrollStatementNotificationChannel[] | null = null,
  ) => {
    if (previewOnly || sendDisabledReason) return false;

    setSendFailures([]);
    // Read before the send: once it lands, every one of them has a published pack.
    const renotify = isRenotifyOnly(selectedRecipients);
    const textCount =
      !channels || channels.includes("text")
        ? getTextableRecipientsCount(selectedRecipients)
        : 0;
    const technicianIds = selectedRecipients.map(
      (recipient) => recipient.technician_id,
    );

    const pendingNoteSaves = selectedRecipients.flatMap((recipient) => {
      const draftNote = noteDrafts[recipient.technician_id];
      if (draftNote === undefined || draftNote === (recipient.note ?? "")) {
        return [];
      }

      return [
        {
          recipient,
          promise: saveNote({
            technicianId: recipient.technician_id,
            note: draftNote,
          }),
        },
      ];
    });
    const noteSaveResults = await Promise.allSettled(
      pendingNoteSaves.map(({ promise }) => promise),
    );
    const failedNoteSaves = noteSaveResults.flatMap((result, index) =>
      result.status === "rejected"
        ? [
            {
              name: pendingNoteSaves[index].recipient.name,
              reason: result.reason,
            },
          ]
        : [],
    );

    if (failedNoteSaves.length > 0) {
      toast.error("Couldn't save the notes", {
        description: `${failedNoteSaves
          .map(({ name }) => name)
          .join(", ")}: ${getErrorMessage(failedNoteSaves[0].reason)}`,
      });
      return false;
    }

    try {
      const response = await sendNotifications({ technicianIds, channels });
      const failedDeliveries = getFailedPayrollStatementDeliveries(
        response.deliveries,
      );
      const failedCount = failedDeliveries.length;

      setSendFailures(failedDeliveries);
      setActiveSendResponse(response);
      trackSubmittedNotes(response.deliveries);

      if (failedCount > 0) {
        toast.error("Some emails couldn't be sent", {
          description: `${failedCount} ${
            failedCount === 1 ? "technician" : "technicians"
          } couldn't be emailed. Review the failures above the recipient list.`,
        });
        return true;
      }

      toast.success(
        renotify ? "Technicians renotified" : "PayPacks published",
        {
          description: getSentDescription({
            count: response.deliveries.length,
            channels,
            textCount,
          }),
        },
      );
      return true;
    } catch (error) {
      setSendFailures([]);
      toast.error("Couldn't send the emails", {
        description: getErrorMessage(error),
      });
      return false;
    }
  };

  return {
    send,
    sendFailures,
    activeSendResponse,
    isSending,
    isSavingNote,
    isSubmitting: isSending || isSavingNote,
  };
};
